import React, { useCallback, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import Button from '../../../shared/components/ui/Button';
import FormInput from '../../../shared/components/ui/FormInput';
import {
  getAllCategories,
  getStateAndCity,
  checkVendorExists,
  importVendor
} from '../../services/api/implementation/impl';
import { useQuery, useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import Multiselect from 'multiselect-react-dropdown';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';
import { capitalizeWords } from '../../utils/stringUtils';

// Define a regex for the pincode validation (6 digits)
const pincodeRegex = /^[0-9]{6}$/;

const validationSchema = yup.object().shape({
  name: yup
    .string()
    .required('Full name is required')
    .min(3, 'Full name should be at least 3 characters')
    .max(50, 'Full name should be at most 50 characters'),
  business_name: yup
    .string()
    .required('Business name is required')
    .max(100, 'Business name should be at most 100 characters'),
  phone_number: yup
    .string()
    .required('Phone number is required')
    .matches(/^[0-9]{10}$/, 'Invalid phone number'),
  email: yup.string().email('Invalid email format').optional(),
  category_id: yup.array().min(1, 'At least one category must be selected'),
  type: yup.string().required('Vendor type is required'),
  zip_code: yup
    .string()
    .required('Zip/Pin code is required')
    .matches(/^[0-9]{6}$/, 'Zip/Pin code should be exactly 6 digits'),
  country: yup.string().optional(),
  state: yup.string().required('State is required'),
  city: yup.string().required('City is required'),
  companyIds: yup.array().of(yup.number().integer().positive()).optional()
});

export const AddVendor = ({
  onClose,
  onSubmit,
  isSubmitting,
  modalType,
  selectedVendor,
  setModalType,
  refetchVendors,
  setOpenAddVendor,
  OpenAddVendor
}) => {
  const userId = Cookies.get('userId');
  // Local state for address details
  const [categories, setCategories] = useState([]);
  const [city, setCity] = useState('Select');
  const [state, setState] = useState('-');
  const [cityDropdownOptions, setCityDropdownOptions] = useState([]);

  // Marketplace check states
  const [marketplaceVendor, setMarketplaceVendor] = useState(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [isCheckingVendor, setIsCheckingVendor] = useState(false);
  const [vendorMessage, setVendorMessage] = useState('');

  // Import states (similar to VendorList)
  const [importData, setImportData] = useState({
    vendor_id: null,
    category_ids: []
  });
  const [showCategories, setShowCategories] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    control,
    watch
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      name: '',
      business_name: '',
      phone_number: '',
      email: '',
      category_id: [],
      type: '',
      zip_code: '',
      country: 'India',
      state: '',
      city: ''
    }
  });

  // Fetch vendor categories
  const { data: allCategories } = useQuery({
    queryKey: ['categories'],
    queryFn: getAllCategories
  });

  // Mutation for checking vendor existence
  const { mutate: checkVendorMutation } = useMutation({
    mutationFn: checkVendorExists,
    onSuccess: (result) => {
      if (result && result?.data?.id) {
        setMarketplaceVendor(result);
        // Extract message from the API response
        const message = result.message || '';
        setVendorMessage(message);
        setShowImportModal(true);
      }
      setIsCheckingVendor(false);
    },
    onError: (err) => {
      setIsCheckingVendor(false);
      // Vendor not found - this is expected, no error message needed
    }
  });

  // Mutation for importing vendor (matches VendorList pattern)
  const { mutate: importVendorMutation, isLoading: isImporting } = useMutation({
    mutationFn: importVendor,
    onSuccess: (result) => {
      toast.success('Vendor imported successfully!');
      setShowImportModal(false);
      setMarketplaceVendor(null);
      setShowCategories(false);
      setOpenAddVendor(false);
      setImportData({ vendor_id: null, category_ids: [] });
      refetchVendors();
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || err?.message || 'Failed to import vendor');
    }
  });

  // Track page view
  useEffect(() => {
    mixpanel.track('Add Vendor Viewed', {
      user_id: userId,
      timestamp: new Date().toISOString()
    });
  }, [userId]);

  useEffect(() => {
    if (allCategories?.data && allCategories?.data?.length > 0) {
      setCategories(allCategories?.data);
    }
  }, [allCategories]);

  // Handler for pincode changes
  const handlePincodeChange = useCallback(
    async (pincode) => {
      const cleanedPincode = pincode.replace(/[^0-9]/g, '');
      setValue('zip_code', cleanedPincode);

      if (cleanedPincode.length === 6) {
        try {
          const pincodeList = await getStateAndCity(cleanedPincode);
          const postOfficeList = pincodeList[0].PostOffice;

          if (postOfficeList.length > 0) {
            const newState = postOfficeList[0].State;
            // Determine unique circles and tehsils from the post office data
            const uniqueCircles = [
              ...new Set(
                postOfficeList
                  .map((office) => {
                    const circle =
                      office.Name && office.Name !== 'NA' && office.Name.trim() !== ''
                        ? office.Name
                        : office.Block;
                    return circle && circle !== 'NA' && circle.trim() !== '' ? circle : null;
                  })
                  .filter(Boolean)
              )
            ];
            const uniqueTehsils = [...new Set(postOfficeList.map((office) => office.Block))];

            setCityDropdownOptions(
              uniqueTehsils.map((tehsil) => ({ value: tehsil, label: tehsil }))
            );
            const newCity = uniqueCircles[0];

            setValue('state', newState);
            setValue('city', uniqueTehsils[0]);

            setState(newState);
            setCity(newCity);
          } else {
            setValue('state', '-');
            setValue('city', 'Select');
            setCityDropdownOptions([]);
            setState('-');
            setCity('Select');
          }
        } catch (error) {
          toast.error('The entered pincode is invalid. Please enter a valid pincode.');
          setValue('state', '-');
          setValue('city', 'Select');
          setCityDropdownOptions([]);
          setState('-');
          setCity('Select');
        }
      }
    },
    [setValue]
  );

  // Handle form submission
  const onSubmitForm = (data) => {
    if (!data.category_id || data.category_id.length < 1) {
      toast.error('Please select atleast one category');
      return;
    }
    const formattedData = {
      name: data.name,
      business_name: data.business_name,
      phone_number: data.phone_number,
      category_ids: data.category_id?.map((item) => item.id),
      type: data.type,
      pincode: data.zip_code,
      country: data.country,
      state: data.state,
      city: data.city
    };

    if (data.email) {
      formattedData.email = data.email;
    }

    onSubmit(formattedData);
    reset();
  };

  useEffect(() => {
    const populateEditForm = async () => {
      if (modalType === 'Edit' && selectedVendor) {
        // Prepare category data for multiselect
        const selectedCategories = selectedVendor.vendorCategoryMapping
          ? selectedVendor.vendorCategoryMapping.map((mapping) => ({
            id: mapping.categroy.id,
            name: mapping.categroy.name.trim()
          }))
          : [];

        // Initial form values
        const initialValues = {
          name: selectedVendor.user?.name || '',
          business_name: selectedVendor.businessName || '',
          phone_number: selectedVendor.user?.phone_number || '',
          email: selectedVendor.user?.email || '',
          category_id: selectedCategories, // Use mapped categories
          type: selectedVendor.type || '',
          zip_code: selectedVendor.pincode || ''
        };

        reset(initialValues);

        // If pincode exists, fetch city options
        const pincodeStr = String(selectedVendor.pincode);
        console.log('Pincode as string:', pincodeStr, 'Length:', pincodeStr.length);

        if (selectedVendor.pincode && pincodeStr.length === 6) {
          // Fetch city options without overwriting existing city
          getStateAndCity(pincodeStr)
            .then((pincodeList) => {
              const postOfficeList = pincodeList[0].PostOffice;
              if (postOfficeList.length > 0) {
                const uniqueTehsils = [...new Set(postOfficeList.map((office) => office.Block))];
                const cityOptions = uniqueTehsils.map((tehsil) => ({
                  value: tehsil,
                  label: tehsil
                }));
                setCityDropdownOptions(cityOptions);

                // Set state
                if (selectedVendor.state) {
                  setState(selectedVendor.state);
                  setValue('state', selectedVendor.state);
                }

                // Set city - preserve existing value
                if (selectedVendor.city) {
                  setCity(selectedVendor.city);
                  setValue('city', selectedVendor.city);
                }
              }
            })
            .catch((error) => {
              console.error('Error fetching pincode data:', error);
              // If fetch fails, still set existing values
              if (selectedVendor.state) {
                setState(selectedVendor.state);
              }
              if (selectedVendor.city) {
                setCity(selectedVendor.city);
              }
            });
        }
      } else {
        // Reset form and state when not in edit mode or no selected vendor
        reset({
          // Reset to default empty/initial values
          name: '',
          business_name: '',
          phone_number: '',
          email: '',
          category_id: [],
          type: '',
          zip_code: '',
          country: 'India',
          state: '',
          city: ''
        });
        setState('-');
        setCityDropdownOptions([]);
        setCity('Select'); // Reset local city state if needed elsewhere
      }
    };

    populateEditForm();
  }, [modalType, selectedVendor, reset]); // Add reset to dependencies

  // Input handlers for sanitizing values
  const handleNameChange = useCallback(
    (val) => {
      const cleanedValue = val.replace(/[^a-zA-Z\s]/g, '').slice(0, 50);
      setValue('name', capitalizeWords(cleanedValue));
    },
    [setValue]
  );

  const handleBusinessNameChange = useCallback(
    (val) => {
      const cleanedValue = val.replace(/[^a-zA-Z0-9\s]/g, '').slice(0, 100);
      setValue('business_name', cleanedValue);
    },
    [setValue]
  );

  const handlePhoneChange = useCallback(
    (val) => {
      const cleanedValue = val.replace(/[^0-9]/g, '').slice(0, 10);
      setValue('phone_number', cleanedValue);

      // Check if vendor exists in marketplace when 10 digits are entered (only for adding, not editing)
      if (cleanedValue.length === 10 && modalType === 'Add') {
        setIsCheckingVendor(true);
        checkVendorMutation(cleanedValue);
      }
    },
    [setValue, checkVendorMutation, modalType]
  );

  // Handle import vendor (follows VendorList pattern)
  const handleImportVendor = () => {
    if (marketplaceVendor?.data) {
      setImportData({ vendor_id: marketplaceVendor?.data?.id, category_ids: [] });
      setShowImportModal(false);
      setShowCategories(true);
    }
  };

  // Handle cancel import - prefill form with marketplace data
  const handleCancelImport = () => {
    setShowImportModal(false);
    setMarketplaceVendor(null);
    setOpenAddVendor(false);
  };

  // Category selection handlers (from VendorList pattern)
  const onCloseImport = () => {
    setImportData({ vendor_id: null, category_ids: [] });
    setShowCategories(false);
  };

  const onCheck = (id, checked) => {
    setImportData((prev) => {
      const category_ids = checked
        ? [...prev.category_ids, id] // add if checked
        : prev.category_ids.filter((catId) => catId !== id); // remove if unchecked

      return { ...prev, category_ids };
    });
  };

  const handleImport = () => {
    const payload = importData;
    importVendorMutation(payload);
  };

  return (
    <>
      <div className="flex flex-col justify-between items-start gap-4">
        <span className="text-lg border-b-[1px] border-stone-200 w-full pb-2">Vendor Details</span>
      </div>
      <form onSubmit={handleSubmit(onSubmitForm)} className="flex flex-col gap-4 pt-6">
        {/* Vendor Information */}
        <div className="flex justify-between items-center w-full">
          <FormInput
            width={48}
            label={'Phone Number'}
            register={register}
            isRequired={true}
            register_key={'phone_number'}
            type={'text'}
            onType={handlePhoneChange}
            errors={errors.phone_number}
            disabled={isCheckingVendor}
            placeholder={isCheckingVendor ? 'Checking marketplace...' : 'Enter phone number'}
          />
          <FormInput
            width={48}
            label={'Email Address'}
            register={register}
            register_key={'email'}
            isRequired={false}
            type={'text'}
            errors={errors.email}
          />
        </div>
        <div className="flex justify-between items-center w-full">
          <FormInput
            width={48}
            label={'Contact Person Name'}
            register_key={'name'}
            minLength={3}
            isRequired={true}
            register={register}
            type={'text'}
            onType={handleNameChange}
            errors={errors.name}
          />
          <FormInput
            width={48}
            label={'Business Name'}
            register_key={'business_name'}
            isRequired={true}
            register={register}
            type={'text'}
            errors={errors.business_name}
            onType={handleBusinessNameChange}
          />
        </div>

        <div className="flex justify-between items-center w-full">
          <div className="w-[48%]">
            <label className="text-gray-600 mb-1 block">Vendor Category *</label>
            <Controller
              name="category_id"
              control={control}
              render={({ field }) => (
                <Multiselect
                  options={categories?.map((category) => ({
                    id: category.id,
                    name: category.name.trim()
                  }))}
                  selectedValues={field.value}
                  onSelect={(selectedList) => {
                    console.log('Selected categories:', selectedList);
                    field.onChange(selectedList);
                  }}
                  onRemove={(selectedList) => {
                    console.log('Updated categories:', selectedList);
                    field.onChange(selectedList);
                  }}
                  displayValue="name"
                  placeholder=""
                  showCheckbox
                />
              )}
            />
            {errors.category_id && (
              <span className="text-red-500">{errors.category_id.message}</span>
            )}
          </div>
          <FormInput
            width={48}
            label={'Vendor Type'}
            isRequired={true}
            register={register}
            register_key={'type'}
            type={'dropdown'}
            options={[
              { label: 'Select Type', value: '', disabled: true, selected: true },
              ...['individual', 'company'].map((item) => ({ label: item, value: item }))
            ]}
            errors={errors.type}
          />
        </div>

        {/* Address Details */}
        <div className="flex flex-col justify-between items-start gap-4">
          <span className="text-lg border-b-[1px] border-stone-200 w-full pb-2">
            Address Details
          </span>
        </div>
        <div className="flex flex-col gap-2">
          <div className="flex justify-between space-x-4">
            <FormInput
              width={48}
              register={register}
              isRequired
              register_key={'zip_code'}
              label={'Zip/Pin Code'}
              type={'text'}
              maxLength={6}
              errors={errors.zip_code}
              onType={handlePincodeChange}
            />
            <FormInput
              width={48}
              register={register}
              isRequired
              register_key={'country'}
              label={'Select Country'}
              valuePre={'India'}
              errors={errors.country}
              CustomStyle={{ pointerEvents: 'none' }}
            />
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <div className="flex justify-between space-x-4">
            <FormInput
              width={48}
              register={register}
              isRequired
              register_key={'state'}
              label={'State'}
              errors={errors.state}
              valuePre={state}
              CustomStyle={{ pointerEvents: 'none' }}
            />
            <FormInput
              width={48}
              register={register}
              isRequired
              register_key={'city'}
              label={'City'}
              valuePre={city}
              type={'dropdown'}
              options={cityDropdownOptions}
              errors={errors.city}
            />
          </div>
        </div>

        {/* Form Action Buttons */}
        <div className="w-full flex justify-end items-center space-x-4 mt-8">
          <Button type="button" onClick={onClose} displayType="secondary">
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {modalType === 'Add' ? 'Add' : modalType === 'Edit' ? 'Update' : ''}
          </Button>
        </div>
      </form>

      {/* Import Confirmation Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center mb-4">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                <svg
                  className="w-6 h-6 text-blue-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900">
                {vendorMessage || 'Vendor Found'}
              </h3>
            </div>

            <div className="mb-4">
              <p className="text-gray-600 mb-2">
                {vendorMessage?.includes('your list')
                  ? 'A vendor with this phone number already exists in your list:'
                  : 'A vendor with this phone number already exists in the marketplace:'}
              </p>
              <div className="bg-gray-50 p-3 rounded-md">
                <p className="font-medium text-gray-900">
                  {marketplaceVendor?.data?.user?.name || marketplaceVendor?.data?.name || 'N/A'}
                </p>
                <p className="text-sm text-gray-600">
                  {marketplaceVendor?.data?.user?.phone_number ||
                    marketplaceVendor?.data?.phone_number ||
                    'N/A'}
                </p>
                <p className="text-sm text-gray-600">
                  {marketplaceVendor?.data?.user?.email || marketplaceVendor?.data?.email || 'N/A'}
                </p>
              </div>
              <p className="text-gray-600 mt-2">
                {vendorMessage?.includes('your list')
                  ? 'This vendor is already in your list.'
                  : 'Would you like to import this vendor instead of creating a new one?'}
              </p>
            </div>
            <div className="flex justify-end space-x-3">
              <Button displayType="secondary" onClick={handleCancelImport} disabled={isImporting}>
                Cancel
              </Button>
              {vendorMessage?.includes('marketplace') && (
                <Button onClick={handleImportVendor} disabled={isImporting}>
                  {isImporting ? 'Importing...' : 'Import Vendor'}
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Categories Selection Modal */}
      {showCategories && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Select Categories</h3>
              <button onClick={onCloseImport} className="text-gray-400 hover:text-gray-600">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <div className="mb-4">
              <p className="text-gray-600 mb-4">
                Select the categories for the vendor you&apos;re importing:
              </p>
              <div className="max-h-60 overflow-y-auto">
                {categories?.map((category) => (
                  <label key={category.id} className="flex items-center mb-2">
                    <input
                      type="checkbox"
                      checked={importData.category_ids.includes(category.id)}
                      onChange={(e) => onCheck(category.id, e.target.checked)}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-700">{category.name}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <Button displayType="secondary" onClick={onCloseImport} disabled={isImporting}>
                Cancel
              </Button>
              <Button
                onClick={handleImport}
                disabled={isImporting || importData.category_ids.length === 0}>
                {isImporting ? 'Importing...' : 'Import Vendor'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AddVendor;
